import { DropdownOption } from '@tcl-models/models';
import { GrayscalePalette } from '@tcl-boron-colors/colors';
import { PatientCohortMatchWorkflowState } from '@tempus/stateflow-types';
import {
  PROGRAM_TYPES_DEC,
  TrialType,
  LfsTrialStatus,
  TimePatientStatuses,
  TimePatientStatusesDetail,
  TimePatientStatusesOrder,
  TimeProgramSiteStatus,
  PatientCohortMatchStates,
  Tab,
} from '@tempus/trials-types';
import React from 'react';

import { createTranslator, type LocaleCode, type Translate } from '~/i18n';
import { convertSnakeToSentenceCase } from '~/utils/misc';

import { PatientCohortMatchUpdates } from '../api/types';
import { PatientTrackerStateId } from '../api/types/patientTrackerStore';

function localized<T>(locale: LocaleCode, build: (t: Translate) => T): T {
  return build(createTranslator(locale));
}

export enum VisitType {
  PROVIDER_VISIT = 'Provider Visit',
  SCAN = 'Scan',
  OTHER = 'Other',
}

export enum PATIENT_TRACKER_TAB {
  REVIEW_REQUIRED = 'Review required',
  WATCHLIST = 'Watchlist',
  CANDIDATE_NOW = 'Candidate now',
  SCREENING_QUEUE = 'Screening queue',
  PENDING_TEMPUS_REVIEW = 'Pending Tempus review',
  ENROLLED = 'Enrolled',
  VISITING_THIS_WEEK = 'Visiting this week',
  TIME_SENSITIVE = 'Time sensitive patients',
  ALL = 'All',
}

export enum SORT_OPTION {
  URGENCY = 'Urgency',
  UPCOMING_VISIT_SOONEST_FIRST = 'Upcoming visit (soonest first)',
  UPCOMING_VISIT_FURTHEST_FIRST = 'Upcoming visit (furthest first)',
  OLDEST = 'Date added (oldest first)',
  NEWEST = 'Date added (newest first)',
}

const SORT_VALUE = {
  URGENCY: 'URGENCY',
  UPCOMING_VISIT_SOONEST_FIRST: 'UPCOMING_VISIT_SOONEST_FIRST',
  UPCOMING_VISIT_FURTHEST_FIRST: 'UPCOMING_VISIT_FURTHEST_FIRST',
  OLDEST: 'OLDEST',
  NEWEST: 'NEWEST',
} as const;

/** Localized labels. `value` stays the stable sort key stored on the tracker filters. */
export const getSortDropdownOptions = (locale: LocaleCode, tab: PATIENT_TRACKER_TAB): DropdownOption[] =>
  localized(locale, (t) => {
    if (tab === PATIENT_TRACKER_TAB.VISITING_THIS_WEEK) {
      return [
        { label: t('tables.sort.upcomingVisitSoonest'), value: SORT_VALUE.UPCOMING_VISIT_SOONEST_FIRST },
        { label: t('tables.sort.upcomingVisitFurthest'), value: SORT_VALUE.UPCOMING_VISIT_FURTHEST_FIRST },
      ];
    }

    return [
      { label: t('tables.sort.urgency'), value: SORT_VALUE.URGENCY },
      { label: t('tables.sort.upcomingVisitSoonest'), value: SORT_VALUE.UPCOMING_VISIT_SOONEST_FIRST },
      { label: t('tables.sort.upcomingVisitFurthest'), value: SORT_VALUE.UPCOMING_VISIT_FURTHEST_FIRST },
      { label: t('tables.sort.dateAddedOldest'), value: SORT_VALUE.OLDEST },
      { label: t('tables.sort.dateAddedNewest'), value: SORT_VALUE.NEWEST },
    ];
  });

const SORT_OPTION_BY_VALUE: Record<string, SORT_OPTION> = {
  [SORT_VALUE.URGENCY]: SORT_OPTION.URGENCY,
  [SORT_VALUE.UPCOMING_VISIT_SOONEST_FIRST]: SORT_OPTION.UPCOMING_VISIT_SOONEST_FIRST,
  [SORT_VALUE.UPCOMING_VISIT_FURTHEST_FIRST]: SORT_OPTION.UPCOMING_VISIT_FURTHEST_FIRST,
  [SORT_VALUE.OLDEST]: SORT_OPTION.OLDEST,
  [SORT_VALUE.NEWEST]: SORT_OPTION.NEWEST,
};

/** Resolve a dropdown option to the English sort enum. Prefers the stable value over a translated label. */
export const sortOptionFromDropdown = (option?: { label?: string; value?: unknown } | null): SORT_OPTION => {
  const fromValue = option?.value == null ? undefined : SORT_OPTION_BY_VALUE[String(option.value)];
  return fromValue ?? (option?.label as SORT_OPTION);
};

const TRIAL_STATUS_FILTER_LABEL_EN: Record<string, string> = {
  [LfsTrialStatus.ACTIVE]: 'Active at my site',
  [LfsTrialStatus.AVAILABLE_TO_ACTIVATE]: 'Available to activate',
  [LfsTrialStatus.ON_HOLD]: 'On hold',
  [TimeProgramSiteStatus.CLOSED]: 'Closed',
};

const patientTrackerTrialStatusLabelWithTranslator = (t: Translate, status: string): string => {
  switch (status) {
    case LfsTrialStatus.ACTIVE:
      return t('tables.patientTracker.trialStatusActiveAtSite');
    case LfsTrialStatus.AVAILABLE_TO_ACTIVATE:
      return t('tables.patientTracker.trialStatusAvailableToActivate');
    case TimeProgramSiteStatus.CLOSED:
      return t('tables.patientTracker.trialStatusClosed');
    default:
      return TRIAL_STATUS_FILTER_LABEL_EN[status] ?? status;
  }
};

export const patientTrackerTrialStatusLabel = (locale: LocaleCode, status: string): string =>
  localized(locale, (t) => patientTrackerTrialStatusLabelWithTranslator(t, status));

/** Match-review filter sent as `unscreened`; trials API maps it to Site Trials screening queue. */
export const UNSCREENED_MATCH_STATUS = 'unscreened';

export const getMatchStatusDropdownOptions = (
  isInternal: boolean = false,
  options?: { includeUnscreened?: boolean },
): DropdownOption[] => {
  const sortedMatchStatuses: Array<PatientCohortMatchWorkflowState | typeof UNSCREENED_MATCH_STATUS> = [];

  if (options?.includeUnscreened) {
    sortedMatchStatuses.push(UNSCREENED_MATCH_STATUS);
  }

  sortedMatchStatuses.push(
    PatientCohortMatchWorkflowState.PENDING_TEMPUS_REVIEW,
    PatientCohortMatchWorkflowState.PENDING_SITE_REVIEW,
    PatientCohortMatchWorkflowState.WATCHLIST,
    PatientCohortMatchWorkflowState.IMMINENT_CANDIDATE,
    PatientCohortMatchWorkflowState.PURSUING_ACTIVATION,
    PatientCohortMatchWorkflowState.CONSENTED,
    PatientCohortMatchWorkflowState.ENROLLED,
    PatientCohortMatchWorkflowState.NO_LONGER_A_CANDIDATE,
  );

  if (isInternal) {
    sortedMatchStatuses.push(PatientCohortMatchWorkflowState.INTERNAL_REVIEW);
  }

  return sortedMatchStatuses.map((status) => ({
    label: getPatientCohortMatchWorkflowStateToDisplay(status),
    value: status,
  }));
};

export const getTrialStatusDropdownOptions = (locale: LocaleCode): DropdownOption[] =>
  localized(locale, (t) => {
    const options = [LfsTrialStatus.ACTIVE, LfsTrialStatus.AVAILABLE_TO_ACTIVATE, TimeProgramSiteStatus.CLOSED];

    return options.map((value) => ({
      label: patientTrackerTrialStatusLabelWithTranslator(t, value),
      value,
    }));
  });

export const getVisitTypeDropdownOptions = (): DropdownOption[] =>
  Object.keys(VisitType).map((key) => ({
    label: VisitType[key],
    value: VisitType[key],
  }));

export const getTrialTypeDropdownOptions = (): DropdownOption[] =>
  Object.keys(TrialType).map((key) => ({
    label: TrialType[key],
    value: TrialType[key],
  }));

/** Default Redux filter values; labels are English fallbacks — localize in UI via `getPatientTrackerProgramOptions`. */
export const PATIENT_TRACKER_PROGRAM_OPTIONS: DropdownOption[] = [
  { label: 'TIME trials', value: String(PROGRAM_TYPES_DEC.TIME) },
  { label: 'Site trials', value: String(PROGRAM_TYPES_DEC.ST) },
];

export const getPatientTrackerProgramOptions = (locale: LocaleCode): DropdownOption[] =>
  localized(locale, (t) => [
    { label: t('tables.patientTracker.programTimeTrials'), value: String(PROGRAM_TYPES_DEC.TIME) },
    { label: t('tables.patientTracker.programSiteTrials'), value: String(PROGRAM_TYPES_DEC.ST) },
  ]);

/** Both selected sends TIME and site trials. An empty selection is treated as both. */
export const patientTrackerProgramTypesForRequest = (programs?: DropdownOption[]): number[] => {
  const selected = new Set((programs ?? []).map((option) => Number(option.value)));
  const hasTime = selected.has(PROGRAM_TYPES_DEC.TIME);
  const hasSite = selected.has(PROGRAM_TYPES_DEC.ST);

  if (hasTime && !hasSite) {
    return [PROGRAM_TYPES_DEC.TIME];
  }

  if (hasSite && !hasTime) {
    return [PROGRAM_TYPES_DEC.ST];
  }

  return [PROGRAM_TYPES_DEC.TIME, PROGRAM_TYPES_DEC.ST];
};

export const getPatientStatusDropdownOptions = (): DropdownOption[] =>
  Object.keys(TimePatientStatuses)
    .sort((statusA, statusB) => TimePatientStatusesOrder[statusA] - TimePatientStatusesOrder[statusB])
    .map((key) => ({
      label: TimePatientStatuses[key],
      value: TimePatientStatuses[key],
      subContent: React.createElement(
        'div',
        { style: { color: GrayscalePalette[600] } },
        TimePatientStatusesDetail[key],
      ),
    }));

export const tabNamesForPage = {
  [PatientTrackerStateId.MATCH_REVIEW_PAGE]: [
    Tab.VISITING_THIS_WEEK,
    Tab.REVIEW_REQUIRED,
    Tab.CANDIDATE_NOW,
    Tab.TIME_SENSITIVE,
  ],
  [PatientTrackerStateId.ALL_PATIENTS_PAGE]: [Tab.WATCHLIST, Tab.ENROLLED, Tab.ALL],
  [PatientTrackerStateId.TEMPUS_NURSE_PAGE]: [Tab.SCREENING_QUEUE, Tab.PENDING_TEMPUS_REVIEW],
  [PatientTrackerStateId.MATERIALS_PAGE]: [
    Tab.VISITING_THIS_WEEK,
    Tab.REVIEW_REQUIRED,
    Tab.CANDIDATE_NOW,
    Tab.TIME_SENSITIVE,
    Tab.ENROLLED,
    Tab.ALL,
  ],
};

export enum MATCH_STATUS_FLAGS {
  NEW = 'New',
  URGENT = 'Urgent',
  COHORT_ON_HOLD = 'Cohort on hold',
  SITE_ON_HOLD = 'Site on hold',
  RESCREEN = 'Rescreen',
}

export const MATCH_STATUS_FLAG_BADGE_VARIANTS = {
  [MATCH_STATUS_FLAGS.NEW]: 1,
  [MATCH_STATUS_FLAGS.URGENT]: 8,
  [MATCH_STATUS_FLAGS.COHORT_ON_HOLD]: 4,
  [MATCH_STATUS_FLAGS.SITE_ON_HOLD]: 4,
  [MATCH_STATUS_FLAGS.RESCREEN]: 4,
};

export enum InternalReviewScreeningOpt {
  REQUEST_RESCREEN_SCREEN_LATER = 'Request rescreen - screen later',
  REQUEST_RESCREEN_CASE_REVIEW = 'Request rescreen - case review',
  RESET_TO_UNSCREENED = 'Reset to unscreened',
}

export const getPatientCohortMatchWorkflowStateToDisplay = (
  state: PatientCohortMatchStates | PatientCohortMatchWorkflowState | 'Candidate now' | typeof UNSCREENED_MATCH_STATUS,
) => {
  switch (state) {
    case PatientCohortMatchWorkflowState.INTERNAL_REVIEW_NOT_ELIGIBLE:
      return 'Not a match';
    case PatientCohortMatchWorkflowState.PENDING_TEMPUS_REVIEW:
      return 'Pending Tempus review';
    case 'Candidate now':
      return 'Candidate now';
    case UNSCREENED_MATCH_STATUS:
      return 'Unscreened';
    default:
      return convertSnakeToSentenceCase(state);
  }
};

export const relevantFieldsForStatus: Record<
  PatientCohortMatchWorkflowState | InternalReviewScreeningOpt,
  (keyof PatientCohortMatchUpdates)[]
> = {
  [PatientCohortMatchWorkflowState.PRE_MATCH]: [],
  [PatientCohortMatchWorkflowState.INTERNAL_REVIEW]: [],
  [InternalReviewScreeningOpt.REQUEST_RESCREEN_SCREEN_LATER]: ['screenLaterDate', 'internalRnToScreeningRnNote'],
  [InternalReviewScreeningOpt.REQUEST_RESCREEN_CASE_REVIEW]: ['internalRnToScreeningRnNote'],
  [InternalReviewScreeningOpt.RESET_TO_UNSCREENED]: ['internalRnToScreeningRnNote'],
  [PatientCohortMatchWorkflowState.INTERNAL_REVIEW_NOT_ELIGIBLE]: [],
  [PatientCohortMatchWorkflowState.PENDING_TEMPUS_REVIEW]: [],
  [PatientCohortMatchWorkflowState.PENDING_SITE_REVIEW]: [],
  [PatientCohortMatchWorkflowState.WATCHLIST]: [
    'snoozeType',
    'snoozeDate',
    'nextVisitType',
    'nextVisitDate',
    'preconsentDate',
  ],
  [PatientCohortMatchWorkflowState.IMMINENT_CANDIDATE]: ['nextVisitType', 'nextVisitDate', 'preconsentDate'],
  [PatientCohortMatchWorkflowState.PURSUING_ACTIVATION]: ['nextVisitType', 'nextVisitDate', 'preconsentDate'],
  [PatientCohortMatchWorkflowState.CONSENTED]: [
    'patientConsentedDate',
    'dateConsentRecognizedByTempus',
    'nextVisitType',
    'nextVisitDate',
  ],
  [PatientCohortMatchWorkflowState.ENROLLED]: ['firstTreatmentDate'],
  [PatientCohortMatchWorkflowState.NO_LONGER_A_CANDIDATE]: ['reasonNotAMatch', 'reasonNotAMatchDetails'],
  [PatientCohortMatchWorkflowState.MERGED]: [],
  [PatientCohortMatchWorkflowState.CANCELED]: [],
};

export const internalControlFields = [
  'tempusToPortalLatestPortalPatientUpdate',
  'biomarkers',
  'urgentFlag',
  'rescreenFlag',
  'noCredit',
  'dateRnClearedForNotification',
  'dateNotificationSentToSite',
  'patientConsentedDate',
  'dateConsentRecognizedByTempus',
  'firstTreatmentDate',
  'internalRnToScreeningRnNote',
  'preconsentDate',
  'preconsentRecognizedDate',
  'identifiedBy',
  'recruitmentVendor',
];

export enum COHORT_MATCH_CHANGE_SOURCE_COMPONENTS {
  MatchStatusUpdate = 'MatchStatusUpdate',
  PreconsentSection = 'PreconsentSection',
  WhyThisStatus = 'WhyThisStatus',
  InternalControls = 'InternalControls',
}
